import React from "react";

import { I18n } from "i18n-js";
import styled from "styled-components";

import * as locales from "../../../../locales.json";
import {
  StyledLink,
  StyledLinkSpan,
  StyledModalBody,
  StyledModalContents,
} from "../lib/styles";
import Modal from "../../../reusable/reactModal";
import { redesignTheme } from "../../../theme/theme";
import ExplorePlansItem from "./explore-plans-item";
import { OpenPortal } from "../lib/types";

const i18n = new I18n(locales);
i18n.defaultLocale = "en";
i18n.locale = (window as any).locale || "";

const StyledExplorePlansLink = styled(StyledLink)`
  font-size: ${redesignTheme.fontSize[50]};
  font-weight: ${redesignTheme.fontWeight.normal};
  position: absolute;
  right: ${redesignTheme.spacing[6]};
  top: 0;
`;

const StyledPlanWrapper = styled.div`
  display: flex;
  gap: ${redesignTheme.spacing[1.5]};
  padding-bottom: ${redesignTheme.spacing[3.5]};
`;

const StyledFooter = styled(StyledLinkSpan)`
  display: block;
  margin-top: ${redesignTheme.spacing[1.5]};
`;

const OFFICE_HOURS_LINK =
  "https://bonusly.zoom.us/webinar/register/WN_-GQuC-XGQ6iL1aySjLdnZA";

const PLANS = ["tier_one", "tier_two", "tier_three"];

interface ExplorePlansProps {
  paymentMethodOnFile: boolean;
  subscriptionType: string;
  trialing: boolean;
  isCancelled: boolean;
  openPortal: OpenPortal;
  openCancel: () => void;
  openReactivate: () => void;
  setRefetchData: (refetchData: boolean) => void;
}

const ExplorePlans = ({
  paymentMethodOnFile,
  subscriptionType,
  trialing,
  isCancelled,
  openPortal,
  openCancel,
  openReactivate,
  setRefetchData,
}: ExplorePlansProps): React.ReactElement => {
  const [showModal, setShowModal] = React.useState(false);

  const openModal = (): void => {
    window.amplitude.track("Admin Billing Explore Plans Modal Opened");
    setShowModal(true);
  };

  const closeModal = (): void => {
    window.amplitude.track("Admin Billing Explore Plans Modal Closed");
    setShowModal(false);
  };

  const productTier = subscriptionType.replace(" ", "_").toLowerCase();

  return (
    <>
      <StyledExplorePlansLink onClick={openModal}>
        {i18n.translate("admin_pages.settings.explore_plans")}
      </StyledExplorePlansLink>
      <Modal
        ariaLabel={`${i18n.translate(
          "admin_pages.settings.explore_plans"
        )} modal`}
        close={closeModal}
        showDialog={showModal}
        title={i18n.translate("admin_pages.settings.explore_bonusly_plans")}
        style={{ width: "100%", maxWidth: "960px" }}
      >
        <StyledModalBody>
          <StyledModalContents>
            <StyledPlanWrapper>
              {PLANS.map((tier) => (
                <ExplorePlansItem
                  key={tier}
                  title={i18n.translate(`admin_pages.plans.${tier}.title`)}
                  description={i18n.translate(
                    `admin_pages.plans.${tier}.description`
                  )}
                  price={i18n.translate(`admin_pages.plans.${tier}.price`)}
                  plusCost={i18n.translate(
                    `admin_pages.plans.${tier}.plus_cost`
                  )}
                  featuresTitle={i18n.translate(
                    `admin_pages.plans.${tier}.features_title`
                  )}
                  features={
                    i18n.translate(`admin_pages.plans.${tier}.features`, {
                      returnObjects: true,
                    }) as string[]
                  }
                  isActive={tier === productTier}
                  hasPaymentMethod={paymentMethodOnFile}
                  trialing={trialing}
                  isCancelled={isCancelled}
                  openPortal={openPortal}
                  openCancel={openCancel}
                  openReactivate={openReactivate}
                  closeModal={closeModal}
                  setRefetchData={setRefetchData}
                />
              ))}
            </StyledPlanWrapper>

            {trialing && (
              <StyledFooter>
                <strong>
                  {i18n.translate("admin_pages.settings.which_plan")}
                </strong>{" "}
                <a href={OFFICE_HOURS_LINK} rel="noreferrer" target="_blank">
                  {i18n.translate("admin_pages.settings.join_office_hours")}
                </a>{" "}
                {i18n.translate("admin_pages.settings.find_perfect_fit")}
              </StyledFooter>
            )}
          </StyledModalContents>
        </StyledModalBody>
      </Modal>
    </>
  );
};

export default ExplorePlans;
